import React, { useEffect, useState, useRef } from "react";
import { Box, CircularProgress, Link, Typography, Avatar } from "@mui/material";
import { formatPhoneNumber } from "../util";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import LocalAtmOutlinedIcon from "@mui/icons-material/LocalAtmOutlined";
import bkstApi from "../../api";
import PhoneField from "../input/PhoneField";

export default function CustomerLookup(props) {
  const { value, excludeTypes } = props;
  const suggestionsRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);

  const useDebounce = (callback, delay) => {
    const timeoutRef = useRef(null);

    useEffect(() => {
      // Cleanup the previous timeout on re-render
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, []);

    const debouncedCallback = (...args) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    };

    return debouncedCallback;
  };

  const handleSearch = useDebounce((query) => {
    callApi(query);
  }, 500);

  const callApi = (query) => {
    setLoading(true);
    bkstApi(`/my/lookup?q=${query}`).then((res) => {
      const results = res.data;
      let rows = results.filter((o) => o.phone.includes(value));
      if (Array.isArray(excludeTypes) && excludeTypes.length > 0) {
        rows = rows.filter((o) => !excludeTypes.includes(o.type));
      }
      setOptions(rows);
      setLoading(false);
    });
  };

  const handleClick = (o) => {
    setOptions([]);

    props.onChange({
      phone: o.phone,
      firstName: o.firstName,
      lastName: o.lastName,
      email: o.email,
      email_cc: o.email_cc,
      type: o.type,
      paymentTerms: o.paymentTerms,
    });
    document.activeElement.blur();
  };

  // Add click outside functionality
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setOptions([]);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const getListItem = (option) => {
    const orderCount = +option.summary?.orders?.past + +option.summary?.orders?.future;
    const futureQuotesCount = +option.summary?.quotes?.future;

    let summary = (
      <Box>
        <Box sx={{ display: "flex", gap: "10px" }}>
          <Box>
            <Avatar>
              {option.firstName?.[0]}
              {option.lastName?.[0]}
            </Avatar>
          </Box>
          <Box>
            <Typography style={{ display: "flex", alignItems: "center", fontSize: ".9em" }}>
              {option.firstName} {option.lastName}
            </Typography>
            <Typography variant="overline" display="block" color="textSecondary">
              {formatPhoneNumber(option.phone) || ""}
            </Typography>
          </Box>
        </Box>
        <Box>
          {orderCount > 0 && (
            <Typography
              variant="overline"
              color="textSecondary"
              style={{ display: "flex", alignItems: "center", gap: "5px" }}
            >
              <ShoppingBagOutlinedIcon sx={{ fontSize: "16px", color: "#999" }} />
              {orderCount} Orders{" "}
              {+option.summary.orders.future > 0
                ? ` (${option.summary.orders.future} upcoming)`
                : ""}
            </Typography>
          )}
          {futureQuotesCount > 0 && (
            <Typography
              variant="overline"
              color="textSecondary"
              style={{ display: "flex", alignItems: "center", gap: "5px" }}
            >
              <LocalAtmOutlinedIcon sx={{ fontSize: "16px", color: "#999" }} />
              {futureQuotesCount} Quotes
            </Typography>
          )}
        </Box>
      </Box>
    );

    return (
      <Link
        key={option.objectID}
        style={{ textDecoration: "none", color: "inherit" }}
        onClick={() => handleClick(option)}
      >
        <Box m={1} px={1} py={1} sx={{ backgroundColor: "#f7f7f7" }}>
          {summary}
        </Box>
      </Link>
    );
  };

  return (
    <Box ref={suggestionsRef} sx={{ position: "relative" }}>
      <PhoneField
        value={value}
        onChange={(phone) => {
          props.onChange({ phone });
          if (phone.length > 3) {
            handleSearch(phone);
          }
        }}
      />

      {options.length > 0 && (
        <Box
          sx={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1000,
            backgroundColor: "white",
            border: "1px solid #ddd",
            borderRadius: "4px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            maxHeight: "300px",
            overflowY: "auto",
            mt: 1,
          }}
        >
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", padding: "20px" }}>
              <CircularProgress size={20} />
            </Box>
          ) : options.length > 0 ? (
            options.map(getListItem)
          ) : (
            <Box sx={{ padding: "12px", textAlign: "center" }}>
              <Typography variant="body2" color="textSecondary">
                No contacts found
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
